import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Loader2, Trash2, Eye, Mail, MessageCircle, Phone, Send, Search } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { sendWhatsApp } from "@/lib/whatsapp";

const CHANNELS = [
  { v: "whatsapp", l: "WhatsApp", icon: MessageCircle },
  { v: "email", l: "Email", icon: Mail },
  { v: "sms", l: "SMS", icon: Phone },
  { v: "internal", l: "Interno", icon: Eye },
];

const COMMON_VARS = [
  "{{client_name}}", "{{client_first_name}}", "{{client_phone}}",
  "{{client_points}}", "{{client_tier}}", "{{tenant_name}}",
  "{{appointment_date}}", "{{service_name}}", "{{service_price}}",
  "{{resource_name}}", "{{nps_link}}", "{{referral_code}}",
];

export default function TenantMessageTemplates() {
  const { slug } = useParams();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [preview, setPreview] = useState(null);
  const [sendPicker, setSendPicker] = useState(null); // { template, clients, filter }
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    name: "", channel: "whatsapp", subject: "", body: "", description: "", is_active: true,
  });

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/tenant/message-templates"); setItems(data.items || []); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm({ name: "", channel: "whatsapp", subject: "", body: "", description: "", is_active: true });
    setOpen(true);
  };
  const openEdit = (t) => {
    setEditing(t);
    setForm({ ...t });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      if (editing) await api.put(`/tenant/message-templates/${editing.id}`, form);
      else await api.post("/tenant/message-templates", form);
      toast.success(editing ? "Template atualizado" : "Template criado");
      setOpen(false); load();
    } catch (er) { toast.error(formatApiError(er)); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Excluir template?")) return;
    await api.delete(`/tenant/message-templates/${id}`); load();
  };

  const doPreview = async (t) => {
    try {
      const { data } = await api.post(`/tenant/message-templates/${t.id}/preview`, {});
      setPreview({ template: t, ...data });
    } catch (er) { toast.error(formatApiError(er)); }
  };

  // Item 7 — Abre picker de cliente e depois wa.me com mensagem renderizada.
  const openSendPicker = async (t) => {
    try {
      const { data } = await api.get("/tenant/clients", { params: { limit: 500 } });
      const withPhone = (data.items || []).filter((c) => c.phone);
      setSendPicker({ template: t, clients: withPhone, filter: "" });
    } catch (er) { toast.error(formatApiError(er)); }
  };

  const sendToClient = async (client) => {
    if (!sendPicker) return;
    try {
      const { data } = await api.post(
        `/tenant/message-templates/${sendPicker.template.id}/preview`,
        { client_id: client.id },
      );
      await sendWhatsApp({
        phone: client.phone || "",
        text: data.body || "",
        clientId: client.id,
        context: "template",
      });
      setSendPicker(null);
    } catch (er) { toast.error(formatApiError(er)); }
  };

  const insertVar = (v) => setForm((f) => ({ ...f, body: (f.body || "") + " " + v }));

  return (
    <div className="p-6 md:p-10 max-w-7xl">
      <div className="flex items-start justify-between mb-8 gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-muted-foreground">Templates de mensagem</div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-1">Comunicação automatizada</h1>
          <p className="text-sm text-muted-foreground mt-1">Crie templates com variáveis. Use-os nas automações Se → Então.</p>
        </div>
        <Button onClick={openCreate} data-testid="tpl-add" className="gap-2"
          style={{ background: `hsl(var(--tenant-primary))`, color: `hsl(var(--tenant-primary-foreground))` }}>
          <Plus className="w-4 h-4" /> Novo template
        </Button>
      </div>

      {loading ? <div className="p-10 text-center"><Loader2 className="w-4 h-4 animate-spin inline" /></div> :
        items.length === 0 ? (
          <Card className="p-12 text-center text-muted-foreground rounded-tenant">
            Nenhum template ainda. Crie templates com variáveis como <code>{"{{client_name}}"}</code> e use-os em automações.
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
            {items.map((t) => {
              const ch = CHANNELS.find((c) => c.v === t.channel) || CHANNELS[0];
              const Icon = ch.icon;
              return (
                <Card key={t.id} className="p-5 rounded-tenant border-border flex flex-col">
                  <div className="flex items-start justify-between mb-2">
                    <Badge variant="outline" className="gap-1.5"><Icon className="w-3.5 h-3.5" /> {ch.l}</Badge>
                    <div className="flex gap-1">
                      <Button size="sm" variant="ghost" onClick={() => doPreview(t)} title="Pré-visualizar"><Eye className="w-4 h-4" /></Button>
                      <Button size="sm" variant="ghost" className="text-destructive" onClick={() => remove(t.id)}><Trash2 className="w-4 h-4" /></Button>
                    </div>
                  </div>
                  <button className="text-left" onClick={() => openEdit(t)}>
                    <div className="font-display font-semibold text-lg">{t.name}</div>
                    <div className="text-xs text-muted-foreground line-clamp-3 mt-1">{t.body}</div>
                  </button>
                  <Button
                    size="sm"
                    className="mt-3 w-full gap-2"
                    onClick={(e) => { e.stopPropagation(); openSendPicker(t); }}
                    data-testid={`tpl-send-wa-${t.id}`}
                    style={{ background: `hsl(var(--tenant-primary))`, color: `hsl(var(--tenant-primary-foreground))` }}
                  >
                    <Send className="w-3.5 h-3.5" /> Enviar via WhatsApp
                  </Button>
                </Card>
              );
            })}
          </div>
        )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-display">{editing ? "Editar template" : "Novo template"}</DialogTitle>
            <DialogDescription>Use <code>{"{{variavel}}"}</code> para inserir dados dinâmicos.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Nome</Label><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="tpl-name" /></div>
              <div className="space-y-1.5">
                <Label>Canal</Label>
                <Select value={form.channel} onValueChange={(v) => setForm({ ...form, channel: v })}>
                  <SelectTrigger data-testid="tpl-channel"><SelectValue /></SelectTrigger>
                  <SelectContent>{CHANNELS.map((c) => <SelectItem key={c.v} value={c.v}>{c.l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            {form.channel === "email" && (
              <div className="space-y-1.5"><Label>Assunto (email)</Label><Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} placeholder="Olá {{client_first_name}}" /></div>
            )}
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label>Corpo da mensagem</Label>
                <span className="text-[10px] text-muted-foreground">Clique para inserir variável ↓</span>
              </div>
              <Textarea required rows={6} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} data-testid="tpl-body" placeholder="Olá {{client_first_name}}, seu atendimento {{service_name}} está confirmado para {{appointment_date}}." />
              <div className="flex flex-wrap gap-1.5 pt-1">
                {COMMON_VARS.map((v) => (
                  <button type="button" key={v} onClick={() => insertVar(v)}
                    className="px-2 py-0.5 text-xs font-mono rounded-md bg-muted hover:bg-muted/70 transition-colors">
                    {v}
                  </button>
                ))}
              </div>
            </div>
            <DialogFooter>
              <Button type="submit" disabled={busy} data-testid="tpl-submit" className="gap-2"
                style={{ background: `hsl(var(--tenant-primary))`, color: `hsl(var(--tenant-primary-foreground))` }}>
                {busy && <Loader2 className="w-4 h-4 animate-spin" />} Salvar
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display">Pré-visualização — {preview?.template?.name}</DialogTitle>
            <DialogDescription>Variáveis substituídas com dados reais de um cliente da sua base.</DialogDescription>
          </DialogHeader>
          {preview?.subject && <div className="text-sm border-b border-border pb-2 mb-2"><span className="text-xs text-muted-foreground">Assunto:</span> <strong>{preview.subject}</strong></div>}
          <div className="text-sm whitespace-pre-wrap bg-muted/40 rounded-md p-4 border border-border">{preview?.body}</div>
        </DialogContent>
      </Dialog>

      {/* Item 7 — Picker de cliente para envio direto no WhatsApp */}
      <Dialog open={!!sendPicker} onOpenChange={(o) => !o && setSendPicker(null)}>
        <DialogContent className="max-w-lg" data-testid="tpl-send-picker">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <MessageCircle className="w-5 h-5" /> Enviar &quot;{sendPicker?.template?.name}&quot;
            </DialogTitle>
            <DialogDescription>
              Selecione o cliente. A mensagem será personalizada com os dados dele e aberta no WhatsApp.
            </DialogDescription>
          </DialogHeader>
          {sendPicker && (
            <div className="space-y-3">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-2.5 text-muted-foreground pointer-events-none" />
                <Input
                  className="pl-9"
                  data-testid="tpl-send-search"
                  placeholder="Buscar por nome ou telefone..."
                  value={sendPicker.filter}
                  onChange={(e) => setSendPicker({ ...sendPicker, filter: e.target.value })}
                  autoFocus
                />
              </div>
              <div className="max-h-96 overflow-y-auto divide-y divide-border">
                {sendPicker.clients.length === 0 && (
                  <div className="text-sm text-muted-foreground text-center py-6">
                    Nenhum cliente com telefone cadastrado ainda.
                  </div>
                )}
                {sendPicker.clients
                  .filter((c) => {
                    if (!sendPicker.filter) return true;
                    const q = sendPicker.filter.toLowerCase();
                    return (c.name || "").toLowerCase().includes(q) || (c.phone || "").includes(q);
                  })
                  .slice(0, 40)
                  .map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => sendToClient(c)}
                      data-testid={`tpl-send-pick-${c.id}`}
                      className="w-full text-left py-2.5 px-2 hover:bg-muted rounded flex items-center justify-between gap-3"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium truncate">{c.name}</div>
                        <div className="text-xs text-muted-foreground flex items-center gap-1"><Phone className="w-3 h-3" /> {c.phone}</div>
                      </div>
                      <Send className="w-4 h-4 text-muted-foreground" />
                    </button>
                  ))}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
